import type {
  ApiFormattedText,
  ApiMessage, ApiMessagePoll, ApiPollResult, ApiPollResults, ApiQuickReply, ApiSponsoredMessage,
  ApiWebPage,
  ApiWebPageFull,
  MediaContent,
} from '../../api/types';
import type {
  MessageList,
  MessageListType,
  ThreadId,
} from '../../types';
import type {
  GlobalState, TabArgs, TabState,
} from '../types';
import { MAIN_THREAD_ID } from '../../api/types';

import {
  IS_MOCKED_CLIENT, IS_TEST, MESSAGE_LIST_SLICE, MESSAGE_LIST_VIEWPORT_LIMIT, TMP_CHAT_ID,
} from '../../config';
import { areDeepEqual } from '../../util/areDeepEqual';
import { addTimestampEntities } from '../../util/dates/timestamp';
import { getCurrentTabId } from '../../util/establishMultitabRole';
import {
  areSortedArraysEqual, excludeSortedArray, omit, omitUndefined, pick, pickTruthy, unique,
} from '../../util/iteratees';
import { isLocalMessageId, type MessageKey } from '../../util/keys/messageKey';
import { unload } from '../../util/mediaLoader';
import {
  getAllMessageMediaHashes,
  getMessageStatefulContent,
  groupMessageIdsByThreadId,
  hasMessageTtl, isMediaLoadableInViewer, mergeIdRanges, orderHistoryIds, orderPinnedIds,
} from '../helpers';
import { getEmojiOnlyCountForMessage } from '../helpers/getEmojiOnlyCountForMessage';
import { getMessageReplyInfo } from '../helpers/replies';
import {
  selectChatMessage,
  selectChatMessages,
  selectChatScheduledMessages,
  selectCurrentMessageIds,
  selectCurrentMessageList,
  selectLinkedChannelId,
  selectListedIds,
  selectMessageIdsByGroupId,
  selectOutlyingLists,
  selectPinnedIds,
  selectPoll,
  selectQuickReplyMessage,
  selectScheduledMessage,
  selectTabState,
  selectViewportIds,
  selectWebPage,
} from '../selectors';
import { selectThreadIdFromMessage, selectThreadInfo, selectThreadLocalStateParam } from '../selectors/threads';
import { removeUnreadMentions } from './chats';
import { removeIdFromSearchResults } from './middleSearch';
import { removeUnreadPollVotes } from './polls';
import { removeUnreadReactions } from './reactions';
import { updateTabState } from './tabs';
import {
  deleteThread,
  replaceTabThreadParam,
  replaceThreadLocalStateParam,
  updateThreadInfo,
  updateThreadInfoMessagesCount,
} from './threads';
import { clearMessageSummary, clearMessageTranslation } from './translations';

type MessageStoreSections = GlobalState['messages']['byChatId'][string];

export function updateCurrentMessageList<T extends GlobalState>(
  global: T,
  chatId: string | undefined,
  threadId: ThreadId = MAIN_THREAD_ID,
  type: MessageListType = 'thread',
  shouldReplaceHistory?: boolean,
  shouldReplaceLast?: boolean,
  ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  const { messageLists } = selectTabState(global, tabId);
  let newMessageLists: MessageList[];
  if (shouldReplaceHistory || (IS_TEST && !IS_MOCKED_CLIENT)) {
    newMessageLists = chatId ? [{ chatId, threadId, type }] : [];
  } else if (chatId) {
    const current = messageLists[messageLists.length - 1];
    if (current?.chatId === chatId && current.threadId === threadId && current.type === type) {
      return global;
    }

    if (current && (current.chatId === TMP_CHAT_ID || shouldReplaceLast)) {
      newMessageLists = [...messageLists.slice(0, -1), { chatId, threadId, type }];
    } else {
      const previous = messageLists[messageLists.length - 2];

      if (previous?.chatId === chatId && previous.threadId === threadId && previous.type === type) {
        newMessageLists = messageLists.slice(0, -1);
      } else {
        newMessageLists = [...messageLists, { chatId, threadId, type }];
      }
    }
  } else {
    newMessageLists = messageLists.slice(0, -1);
  }

  return updateTabState(global, {
    messageLists: newMessageLists,
  }, tabId);
}

export function replaceChatMessages<T extends GlobalState>(
  global: T, chatId: string, newById: Record<number, ApiMessage>,
): T {
  return updateMessageStore(global, chatId, {
    byId: newById,
  });
}

// A message that reached the store without an `id` cannot be rendered — the list keys, sorts and
// date-groups by it — so it occupies its slot in `byId` as an invisible husk, and every later fetch
// skips the id as "already loaded". Seen once on production: a bot reply arrived as
// `{ content: { text: {} }, isProtected: false }` while the chat was being switched, and stayed
// invisible for the rest of the session even though both the history API and `listedIds` had it.
// The producing path is still unknown; dropping the husk here keeps one bad write from hiding a
// real message, because an id with no message is a state the list already handles (it is what
// every unloaded page looks like) while an id with a husk is not.
//
// `updateChatMessage` produces the same husk from the other side: an update that arrives for a
// message the store does not have yet (a reaction, a view count) is built into a body of its own,
// out of the update alone — no `content` to paint and no `date` to place it under a day header.
// Every write to `byId` funnels through here, so such a body cannot enter the store, be persisted,
// or be rehydrated — including one already sitting in a machine's IndexedDB, dropped on read.
//
// A body that has all three but a stripped `content` is deliberately kept: it can still be rendered
// (as the unsupported placeholder) and a later fetch overwrites it with the full content, whereas
// dropping it would also blank the chat-list preview that reads the same body.
function omitHusks(byId: Record<number, ApiMessage>) {
  const ids = Object.keys(byId);
  const hasHusk = ids.some((id) => isMessageHusk(byId[Number(id)]));
  if (!hasHusk) return byId;

  const kept: Record<number, ApiMessage> = {};
  ids.forEach((id) => {
    const message = byId[Number(id)];
    if (!isMessageHusk(message)) kept[Number(id)] = message;
  });
  return kept;
}

export function isMessageHusk(message?: ApiMessage) {
  // The list keys a message by `id`, groups it by `date` and paints it from `content`. A body
  // missing any of the three is not a message the list can place, only a slot that hides one.
  return !message || message.id === undefined || message.date === undefined || !message.content;
}

export function updateMessageStore<T extends GlobalState>(
  global: T, chatId: string, update: Partial<MessageStoreSections>,
): T {
  const sanitized = update.byId ? omitHusks(update.byId) : undefined;
  const safeUpdate = sanitized ? { ...update, byId: sanitized } : update;

  const current = global.messages.byChatId[chatId]
    || { byId: {}, threadsById: {}, summaryById: {} };

  return {
    ...global,
    messages: {
      ...global.messages,
      byChatId: {
        ...global.messages.byChatId,
        [chatId]: {
          ...current,
          ...safeUpdate,
        },
      },
    },
  };
}

// The FE computes `emojiOnlyCount` itself (the backend never sends it). The realtime
// `updateChatMessage` path already sets it, but the add/replace ingest paths stored raw backend
// text, so history-loaded single-emoji messages skipped animated-emoji and enlarged rendering until
// an unrelated update recomputed it. Mirror the update-path computation here, on the freshly parsed
// backend message (safe to mutate — it is a fresh copy owned by the store, not shared).
function ensureEmojiOnlyCount(message: ApiMessage) {
  const { text } = message.content;
  if (!text || text.emojiOnlyCount !== undefined) return;
  text.emojiOnlyCount = getEmojiOnlyCountForMessage(message.content, message.groupedId);
}

export function addMessages<T extends GlobalState>(
  global: T, messages: ApiMessage[], shouldOverwrite = true,
): T {
  const addedByChatId = messages.reduce((messagesByChatId, message: ApiMessage) => {
    if (!messagesByChatId[message.chatId]) {
      messagesByChatId[message.chatId] = {};
    }
    messagesByChatId[message.chatId][message.id] = message;

    return messagesByChatId;
  }, {} as Record<string, Record<number, ApiMessage>>);

  Object.keys(addedByChatId).forEach((chatId) => {
    global = addChatMessagesById(global, chatId, addedByChatId[chatId], shouldOverwrite);
  });

  return global;
}

// `/messaging/topics/list` returns each topic's last message as a partial projection: no `reactions`,
// among other fields. Replacing the stored message with it erased what a full fetch or a realtime
// update had already delivered, so only overlay the fields the projection does carry.
export function addPartialMessages<T extends GlobalState>(global: T, messages: ApiMessage[]): T {
  return addMessages(global, messages.map((message) => ({
    ...selectChatMessage(global, message.chatId, message.id),
    ...message,
  })));
}

// The backend strips some content types (e.g. `dice`) to an empty `content: {}` in preview
// projections like the chat-list "last message" payload, while `history`/`by-ids` return the full
// content. Such a stripped preview must never overwrite a fuller message a real fetch already
// stored, or the bubble renders as "not supported". A genuine message always carries at least one
// content field with something in it, so this uniquely marks a stripped projection — which is also
// what makes it the test for a husk (see `omitHusks`).
function hasStrippedContent(message: ApiMessage) {
  return isStrippedContent(message.content);
}

function isStrippedContent(content?: MediaContent) {
  if (!content) return true;

  const keys = Object.keys(content);
  if (!keys.length) return true;

  // The same stripped shape one level deeper: a `text` that holds no string. Nothing else can render
  // it, and `MessageText` has no string to fall back on either.
  return keys.length === 1 && keys[0] === 'text' && !content.text?.text;
}

export function replaceMessages<T extends GlobalState>(
  global: T, messages: ApiMessage[],
): T {
  const updatedByChatId = messages.reduce((messagesByChatId, message: ApiMessage) => {
    if (!messagesByChatId[message.chatId]) {
      messagesByChatId[message.chatId] = {};
    }
    messagesByChatId[message.chatId][message.id] = message;

    return messagesByChatId;
  }, {} as Record<string, Record<number, ApiMessage>>);

  Object.keys(updatedByChatId).forEach((chatId) => {
    const currentById = selectChatMessages(global, chatId) || {};
    // Merge per message (existing as base, incoming as overlay), not per chat's whole `byId` map —
    // some incoming sources (e.g. the chat list's embedded `lastMessage` preview) omit fields a
    // fuller fetch already established for the same id (like `replyInfo`); a whole-object swap would
    // silently drop them instead of just refreshing the fields the incoming message actually has.
    const newById = { ...currentById };
    Object.entries(updatedByChatId[chatId]).forEach(([id, message]) => {
      const existing = currentById[Number(id)];
      // Keep the fuller stored message rather than blanking it with a stripped preview.
      if (existing && hasStrippedContent(message) && !hasStrippedContent(existing)) {
        return;
      }
      ensureEmojiOnlyCount(message);
      newById[Number(id)] = { ...existing, ...message };
    });
    global = replaceChatMessages(global, chatId, newById);
    // An edit of a channel post comes back with the raw `senderId`/`isOutgoing` again, which the merge
    // above puts back over the channel-post shape — re-stamp so an edited post does not fall back to
    // rendering as a member message.
    global = stampDiscussionRoots(global, chatId, updatedByChatId[chatId]);
  });

  return global;
}

// `shouldOverwrite` (default) lets a fresh fetch (history/by-ids/search/sync — an authoritative,
// full `ApiMessage`) enrich or replace whatever's already stored for that id. Pass `false` only for
// genuinely partial data (e.g. `updateChatLastMessage`'s preview-only message) so it can fill a gap
// but never clobber a fuller message that's already in the store. Before this flag existed, every
// caller favored the already-stored version — a message inserted early as a partial preview (the
// chat-list "last message") could never be enriched by a later full fetch, permanently losing
// fields the preview omits (e.g. `replyInfo`), even after the full message loaded.
export function addChatMessagesById<T extends GlobalState>(
  global: T, chatId: string, newById: Record<number, ApiMessage>, shouldOverwrite = true,
): T {
  const byId = selectChatMessages(global, chatId);

  if (!shouldOverwrite && byId && Object.keys(newById).every((newId) => Boolean(byId[Number(newId)]))) {
    return global;
  }

  Object.values(newById).forEach(ensureEmojiOnlyCount);

  if (shouldOverwrite && byId) {
    // Drop stripped previews that would blank out a fuller stored message (see `hasStrippedContent`).
    const overlay = { ...newById };
    Object.keys(overlay).forEach((id) => {
      const existing = byId[Number(id)];
      if (existing && hasStrippedContent(overlay[Number(id)]) && !hasStrippedContent(existing)) {
        delete overlay[Number(id)];
      }
    });
    global = replaceChatMessages(global, chatId, { ...byId, ...overlay });
  } else {
    global = replaceChatMessages(global, chatId, shouldOverwrite ? {
      ...byId,
      ...newById,
    } : {
      ...newById,
      ...byId,
    });
  }

  global = addMessageThreadInfos(global, newById);
  return stampDiscussionRoots(global, chatId, newById);
}

// Re-run the stamping over everything already stored for a chat. The pointer that identifies a
// discussion group — `linkedChatId` on its full info — can land after its history has been stored, so
// without this the group's copies of channel posts stay rendered as plain member messages until some
// unrelated update happens to touch the store again.
export function stampChatDiscussionRoots<T extends GlobalState>(global: T, chatId: string): T {
  const byId = selectChatMessages(global, chatId);
  if (!byId || !selectLinkedChannelId(global, chatId)) {
    return global;
  }

  return stampDiscussionRoots(global, chatId, byId);
}

// Messages arrive with their thread info embedded (`repliesThreadInfo`), but everything that renders
// a thread reads it from the threads store via `selectThreadInfo`. Mirror it there so the channel
// post's "Leave a comment" button (`isCommentsInfo`) and the discussion group's channel-post shape
// (the group-side info, keyed by the root message) both resolve without a separate discussion fetch.
function addMessageThreadInfos<T extends GlobalState>(global: T, newById: Record<number, ApiMessage>): T {
  for (const message of Object.values(newById)) {
    if (message.repliesThreadInfo) {
      global = updateThreadInfo(global, message.repliesThreadInfo);
    }
  }

  return global;
}

// The linked discussion group's copy of a channel post (a comments-thread root, `id === threadId`) is
// served as a plain member message — same sender as the admin who posted it, no pointer at the channel
// — so it would render as an ordinary group bubble, or as the admin's own outgoing bubble. TheAdvance
// shows it as the linked channel post instead. Stamp the stored root with the channel-post shape the UI
// understands (`isOutgoing` cleared, `senderId` set to the channel, a `forwardInfo` carrying
// `isLinkedChannelPost`) so `Message` renders the channel as the sender with the "DiscussChannel" header.
export function stampDiscussionRoots<T extends GlobalState>(
  global: T, chatId: string, newById: Record<number, ApiMessage>,
): T {
  const linkedChannelId = selectLinkedChannelId(global, chatId);
  const candidateIds = new Set(Object.keys(newById).map(Number));
  // Comments point at their thread root via `replyToTopId`. Outside a forum nothing else sets it — a
  // plain reply carries `replyToMsgId` alone — so inside a channel-linked group it marks the root even
  // before the comments thread has ever been fetched.
  const derivedRootIds = new Set<number>();

  if (linkedChannelId) {
    for (const message of Object.values(newById)) {
      const replyInfo = getMessageReplyInfo(message);
      const { replyToMsgId, replyToTopId } = replyInfo || {};
      if (!replyToTopId) continue;

      candidateIds.add(replyToTopId);
      derivedRootIds.add(replyToTopId);

      // A first-level comment only carries the thread pointer, so nothing resolved a quote for it and
      // it rendered in the group as a message unrelated to the post it answers. Point it at the root
      // as a real reply — the thread view hides a quote of its own root (`replyToMsgId === threadId`),
      // so the quote shows only in the group's main list, which is what upstream does. Guarded against
      // a root whose pointer names itself, which would otherwise render as replying to itself.
      if (!replyToMsgId && replyToTopId !== message.id) {
        global = updateChatMessage(global, chatId, message.id, {
          replyInfo: { ...replyInfo!, replyToMsgId: replyToTopId },
        });
      }
    }
  }

  for (const messageId of candidateIds) {
    const message = selectChatMessage(global, chatId, messageId);
    if (!message) {
      continue;
    }

    const origin = selectLinkedChannelPostOrigin(global, chatId, message, linkedChannelId)
      || (derivedRootIds.has(messageId) ? { fromChannelId: linkedChannelId! } : undefined);
    if (!origin) {
      continue;
    }

    // Remember a root derived from a comment's pointer, so the shape survives a later refetch of the
    // root on its own — that batch carries no comment to re-derive it from.
    if (derivedRootIds.has(messageId) && !selectThreadInfo(global, chatId, messageId)) {
      global = updateThreadInfo(global, {
        isCommentsInfo: false,
        chatId,
        threadId: messageId,
        fromChannelId: origin.fromChannelId,
        fromMessageId: origin.fromMessageId,
      });
    }

    // The origin post's id is what enables the jump-to-original arrow, and it can arrive late: a root
    // derived from a comment's pointer knows only the channel until the thread meta loads. Keep
    // whichever id is already known so a later pass can only ever add it, never drop it.
    const fromMessageId = origin.fromMessageId ?? message.forwardInfo?.channelPostId;
    const isAlreadyStamped = message.forwardInfo?.isLinkedChannelPost
      && message.senderId === origin.fromChannelId && !message.isOutgoing
      && message.forwardInfo.channelPostId === fromMessageId;
    if (isAlreadyStamped) {
      continue;
    }

    global = updateChatMessage(global, chatId, messageId, {
      isOutgoing: false,
      senderId: origin.fromChannelId,
      forwardInfo: {
        date: message.date,
        isChannelPost: true,
        isLinkedChannelPost: true,
        fromChatId: origin.fromChannelId,
        fromId: origin.fromChannelId,
        // Keeps the stamp self-describing, so it survives a merge that puts the raw fields back.
        savedFromPeerId: origin.fromChannelId,
        channelPostId: fromMessageId,
        fromMessageId,
      },
    });
  }

  return global;
}

// Which channel post a discussion-group message mirrors, from whichever pointer the backend gave us:
// the thread meta (an `ApiMessageThreadInfo` the discussion fetch built, or one the backend embedded on
// the message), or a `forwardInfo` naming the group's own linked channel. Returns `undefined` for an
// ordinary group message.
function selectLinkedChannelPostOrigin<T extends GlobalState>(
  global: T, chatId: string, message: ApiMessage, linkedChannelId?: string,
): { fromChannelId: string; fromMessageId?: number } | undefined {
  const threadInfo = selectThreadInfo(global, chatId, message.id);
  if (threadInfo?.isCommentsInfo === false && threadInfo.fromChannelId) {
    return { fromChannelId: threadInfo.fromChannelId, fromMessageId: threadInfo.fromMessageId };
  }

  // `savedFromPeerId` is what separates the auto-forwarded copy from a member forwarding the same post
  // by hand: only the copy the channel itself pushed into its discussion group carries it.
  const { forwardInfo } = message;
  if (linkedChannelId && forwardInfo?.isChannelPost && forwardInfo.savedFromPeerId === linkedChannelId) {
    return { fromChannelId: linkedChannelId, fromMessageId: forwardInfo.fromMessageId };
  }

  return undefined;
}

export const LOCAL_PREVIEW_MEDIA_KINDS = ['photo', 'video', 'document', 'audio', 'voice'] as const;

// Drop a local media preview whose blob URL has just been revoked by the media cache (see
// `onBlobUrlRevoked`). Nothing re-fetches media that carries a `blobUrl` — `getMessageMediaHash`
// returns `undefined` for it — so a message left holding a dead URL renders a black frame for the rest
// of the session. Removing it puts the media back on the normal loading path.
export function dropLocalMediaPreview<T extends GlobalState>(
  global: T, chatId: string, messageId: number, blobUrl: string,
): T {
  const message = selectChatMessage(global, chatId, messageId);
  if (!message) {
    return global;
  }

  let content = message.content;
  LOCAL_PREVIEW_MEDIA_KINDS.forEach((kind) => {
    const media = content[kind] as { blobUrl?: string } | undefined;
    if (media?.blobUrl !== blobUrl) return;
    content = { ...content, [kind]: { ...media, blobUrl: undefined } };
  });

  if (content === message.content) {
    return global;
  }

  return updateChatMessage(global, chatId, messageId, { content });
}

export function updateChatMessage<T extends GlobalState>(
  global: T, chatId: string, messageId: number, messageUpdate: Partial<ApiMessage>, withDeepCheck = false,
): T {
  const byId = selectChatMessages(global, chatId) || {};
  const message = byId[messageId];

  if (withDeepCheck && message) {
    const updateKeys = Object.keys(messageUpdate) as (keyof ApiMessage)[];
    if (areDeepEqual(pick(message, updateKeys), messageUpdate)) {
      return global;
    }
  }

  if (message && messageUpdate.isMediaUnread === false && hasMessageTtl(message)) {
    if (message.content.voice) {
      messageUpdate.content = {
        action: {
          mediaType: 'action',
          type: 'expired',
          isVoice: true,
        },
      };
    } else if (message.content.video?.isRound) {
      messageUpdate.content = {
        action: {
          mediaType: 'action',
          type: 'expired',
          isRoundVideo: true,
        },
      };
    }
  }

  // A stripped payload (see `isStrippedContent`) must not blank out content a fuller fetch already
  // stored — the same rule `addChatMessagesById` and `replaceMessages` follow. Without it the send
  // reconcile of a content type the backend strips (a dice) turns the message into a husk, which the
  // store then drops, and the sender watches their own message disappear.
  const shouldKeepStoredContent = Boolean(
    message && messageUpdate.content
    && isStrippedContent(messageUpdate.content) && !isStrippedContent(message.content),
  );

  let text = message?.content?.text;
  if (messageUpdate.content) {
    const emojiOnlyCount = getEmojiOnlyCountForMessage(
      messageUpdate.content, message?.groupedId || messageUpdate.groupedId,
    );
    text = messageUpdate.content.text ? addTimestampEntities(messageUpdate.content.text) : messageUpdate.content.text;
    if (text) text.emojiOnlyCount = emojiOnlyCount;
  }

  // The enriched `text` (timestamp entities + FE-computed `emojiOnlyCount`) must live at
  // `content.text` — the only place readers look. Nesting it under `content` instead of a top-level
  // `text` (which `ApiMessage` has no field for, so it was silently dropped) is what makes single
  // emojis render enlarged/animated after a realtime update or send reconcile.
  const updatedMessage = omitUndefined({
    ...message,
    ...messageUpdate,
    content: (messageUpdate.content && !shouldKeepStoredContent)
      ? (text ? { ...messageUpdate.content, text } : messageUpdate.content)
      : message?.content,
  });

  if (!updatedMessage.id) {
    return global;
  }

  return replaceChatMessages(global, chatId, {
    ...byId,
    [messageId]: updatedMessage,
  });
}

export function updateScheduledMessage<T extends GlobalState>(
  global: T, chatId: string, messageId: number, messageUpdate: Partial<ApiMessage>,
): T {
  const message = selectScheduledMessage(global, chatId, messageId)!;

  let text = message?.content?.text;
  if (messageUpdate.content) {
    const emojiOnlyCount = getEmojiOnlyCountForMessage(
      messageUpdate.content, message?.groupedId || messageUpdate.groupedId,
    );
    text = messageUpdate.content.text ? addTimestampEntities(messageUpdate.content.text) : messageUpdate.content.text;
    if (text) text.emojiOnlyCount = emojiOnlyCount;
  }

  // Nest the enriched text under `content` (see `updateChatMessage`); a top-level `text` is dropped.
  const updatedMessage = omitUndefined({
    ...message,
    ...messageUpdate,
    content: messageUpdate.content
      ? (text ? { ...messageUpdate.content, text } : messageUpdate.content)
      : message?.content,
  });

  if (!updatedMessage.id) {
    return global;
  }

  return updateScheduledMessages(global, chatId, {
    [messageId]: updatedMessage,
  });
}

export function updateQuickReplyMessage<T extends GlobalState>(
  global: T, messageId: number, messageUpdate: Partial<ApiMessage>,
): T {
  const message = selectQuickReplyMessage(global, messageId);
  const updatedMessage = {
    ...message,
    ...messageUpdate,
  };

  if (!updatedMessage.id) {
    return global;
  }

  return updateQuickReplyMessages(global, {
    [messageId]: updatedMessage,
  });
}

export function deleteQuickReplyMessages<T extends GlobalState>(
  global: T, messageIds: number[],
): T {
  const byId = global.quickReplies.messagesById;
  const newById = omit(byId, messageIds);
  return {
    ...global,
    quickReplies: {
      ...global.quickReplies,
      messagesById: newById,
    },
  };
}

// `shouldKeepMedia` is for the reconciliation paths, where the message is not going away: the
// optimistic copy is dropped only so the server one can take its slot, and both describe the same
// media, so their media hashes are identical. Unloading there revokes the blob URL the sent message
// is still rendering (and drops it from Cache Storage), and because `getMessageMediaHash` returns
// `undefined` for media that carries a local `blobUrl`, nothing ever re-fetches it — the message is
// pinned to a dead URL. The mounted element keeps painting the frame it already decoded, so it looks
// fine until any *new* element is created for that media (media viewer, or scrolling the message out
// of the list and back), which fails with `MEDIA_ERR_SRC_NOT_SUPPORTED` and renders black.
export function deleteChatMessages<T extends GlobalState>(
  global: T,
  chatId: string,
  messageIds: number[],
  shouldKeepMedia = false,
): T {
  const byId = selectChatMessages(global, chatId);
  if (!byId) {
    return global;
  }

  orderHistoryIds(messageIds);
  const updatedThreads = new Map<ThreadId, number[]>();
  updatedThreads.set(MAIN_THREAD_ID, messageIds);

  const mediaIdsToRemove: number[] = [];
  messageIds.forEach((messageId) => {
    const message = byId[messageId];
    if (!message) return;
    if (!shouldKeepMedia) {
      const statefulContent = getMessageStatefulContent(global, message);
      const hashes = getAllMessageMediaHashes(message, statefulContent);
      hashes.forEach((hash) => {
        unload(hash);
      });
    }
    if (isMediaLoadableInViewer(message)) {
      mediaIdsToRemove.push(messageId);
    }
    const threadId = selectThreadIdFromMessage(global, message);
    if (!threadId || threadId === MAIN_THREAD_ID) {
      return;
    }
    const threadMessages = updatedThreads.get(threadId) || [];
    threadMessages.push(messageId);
    updatedThreads.set(threadId, threadMessages);
    global = clearMessageTranslation(global, chatId, messageId);
    global = clearMessageSummary(global, chatId, messageId);
  });

  const deletedForwardedPosts = Object.values(pickTruthy(byId, messageIds)).filter(
    ({ forwardInfo }) => forwardInfo?.isLinkedChannelPost,
  );

  updatedThreads.forEach((threadMessageIds, threadId) => {
    const threadInfo = selectThreadInfo(global, chatId, threadId);

    let listedIds = selectListedIds(global, chatId, threadId);
    let pinnedIds = selectPinnedIds(global, chatId, threadId);
    let outlyingLists = selectOutlyingLists(global, chatId, threadId);
    let newMessageCount = threadInfo?.messagesCount;

    if (listedIds) {
      listedIds = excludeSortedArray(listedIds, threadMessageIds);
    }

    if (outlyingLists) {
      outlyingLists = outlyingLists.map((list) => excludeSortedArray(list, threadMessageIds));
    }

    if (pinnedIds) {
      pinnedIds = excludeSortedArray(pinnedIds, orderPinnedIds(threadMessageIds));
    }

    const nonLocalMessageCount = threadMessageIds.filter((id) => !isLocalMessageId(id)).length;
    if (newMessageCount !== undefined) {
      newMessageCount -= nonLocalMessageCount;
    }

    Object.values(global.byTabId).forEach(({ id: tabId }) => {
      const tabState = selectTabState(global, tabId);
      const activeDownloadsInChat = Object.entries(tabState.activeDownloads).filter(
        ([, { originChatId, originMessageId }]) => originChatId === chatId && originMessageId,
      );

      activeDownloadsInChat.forEach(([mediaHash, context]) => {
        if (messageIds.includes(context.originMessageId!)) {
          global = cancelMessageMediaDownload(global, [mediaHash], tabId);
        }
      });

      mediaIdsToRemove.forEach((mediaId) => {
        global = removeIdFromSearchResults(global, chatId, threadId, mediaId, tabId);
      });

      const viewportIds = selectViewportIds(global, chatId, threadId, tabId);
      if (!viewportIds) return;

      const newViewportIds = excludeSortedArray(viewportIds, messageIds);
      global = replaceTabThreadParam(
        global,
        chatId,
        threadId,
        'viewportIds',
        newViewportIds.length === 0 ? undefined : newViewportIds,
        tabId,
      );
    });

    global = replaceThreadLocalStateParam(global, chatId, threadId, 'listedIds', listedIds);
    global = replaceThreadLocalStateParam(global, chatId, threadId, 'outlyingLists', outlyingLists);
    global = replaceThreadLocalStateParam(global, chatId, threadId, 'pinnedIds', pinnedIds);

    if (threadInfo && newMessageCount !== undefined) {
      global = updateThreadInfoMessagesCount(global, chatId, threadId, newMessageCount);
    }
  });

  if (deletedForwardedPosts.length) {
    Object.values(global.byTabId).forEach(({ id: tabId }) => {
      const currentMessageList = selectCurrentMessageList(global, tabId);
      const canDeleteCurrentThread = currentMessageList && currentMessageList.chatId === chatId
        && currentMessageList.type === 'thread';
      const currentThreadId = currentMessageList?.threadId;

      deletedForwardedPosts.forEach((message) => {
        const { fromChatId, fromMessageId } = message.forwardInfo!;
        const originalPost = selectChatMessage(global, fromChatId!, fromMessageId!);

        if (canDeleteCurrentThread && currentThreadId === message.id) {
          global = updateCurrentMessageList(global, chatId, undefined, undefined, undefined, undefined, tabId);
        }
        if (originalPost) {
          global = deleteThread(global, fromChatId!, fromMessageId!);
        }
      });
    });
  }

  global = removeUnreadReactions({ global, chatId, ids: messageIds });
  global = removeUnreadMentions({ global, chatId, ids: messageIds });
  global = removeUnreadPollVotes({ global, chatId, ids: messageIds });

  const newById = omit(byId, messageIds);
  global = replaceChatMessages(global, chatId, newById);

  return global;
}

export function deleteChatScheduledMessages<T extends GlobalState>(
  global: T,
  chatId: string,
  messageIds: number[],
): T {
  const byId = selectChatScheduledMessages(global, chatId);
  if (!byId) {
    return global;
  }
  const newById = omit(byId, messageIds);

  const groupedByThreadId = groupMessageIdsByThreadId(global, chatId, messageIds, true);
  Object.entries(groupedByThreadId).forEach(([tId, newThreadScheduledIds]) => {
    const threadId = tId as ThreadId;
    const scheduledIds = selectThreadLocalStateParam(global, chatId, threadId, 'scheduledIds');
    if (!scheduledIds?.length) return;

    const cleanedScheduledIds = scheduledIds.filter((id) => !newThreadScheduledIds.includes(id));
    global = replaceThreadLocalStateParam(global, chatId, threadId, 'scheduledIds', cleanedScheduledIds);
  });

  global = {
    ...global,
    scheduledMessages: {
      byChatId: {
        ...global.scheduledMessages.byChatId,
        [chatId]: {
          byId: newById,
        },
      },
    },
  };

  return global;
}

export function updateListedIds<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  idsUpdate: number[],
): T {
  const listedIds = selectListedIds(global, chatId, threadId);
  const newIds = listedIds?.length
    ? idsUpdate.filter((id) => !listedIds.includes(id))
    : idsUpdate;

  if (listedIds && !newIds.length) {
    return global;
  }

  return replaceThreadLocalStateParam(global, chatId, threadId, 'listedIds', orderHistoryIds([
    ...(listedIds || []),
    ...newIds,
  ]));
}

export function removeOutlyingList<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  list: number[],
): T {
  const outlyingLists = selectOutlyingLists(global, chatId, threadId);
  if (!outlyingLists) {
    return global;
  }

  const newOutlyingLists = outlyingLists.filter((l) => l !== list);

  return replaceThreadLocalStateParam(global, chatId, threadId, 'outlyingLists', newOutlyingLists);
}

export function updateOutlyingLists<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  idsUpdate: number[],
): T {
  if (!idsUpdate.length) return global;

  const outlyingLists = selectOutlyingLists(global, chatId, threadId);

  const newOutlyingLists = mergeIdRanges(outlyingLists || [], idsUpdate);

  return replaceThreadLocalStateParam(global, chatId, threadId, 'outlyingLists', newOutlyingLists);
}

export function addViewportId<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  newId: number,
  ...[tabId = getCurrentTabId()]: TabArgs<T>
) {
  const viewportIds = selectViewportIds(global, chatId, threadId, tabId) || [];
  if (viewportIds.includes(newId)) {
    return global;
  }

  const newIds = orderHistoryIds([
    ...(
      viewportIds.length < MESSAGE_LIST_VIEWPORT_LIMIT
        ? viewportIds
        : viewportIds.slice(-(MESSAGE_LIST_SLICE / 2))
    ),
    newId,
  ]);

  return replaceTabThreadParam(global, chatId, threadId, 'viewportIds', newIds, tabId);
}

export function safeReplaceViewportIds<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  newViewportIds: number[],
  ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  const currentIds = selectViewportIds(global, chatId, threadId, tabId) || [];
  const newIds = orderHistoryIds(newViewportIds);

  return replaceTabThreadParam(
    global,
    chatId,
    threadId,
    'viewportIds',
    areSortedArraysEqual(currentIds, newIds) ? currentIds : newIds,
    tabId,
  );
}

export function safeReplacePinnedIds<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  newPinnedIds: number[],
): T {
  const currentIds = selectPinnedIds(global, chatId, threadId) || [];
  const newIds = orderPinnedIds(newPinnedIds);

  return replaceThreadLocalStateParam(
    global,
    chatId,
    threadId,
    'pinnedIds',
    areSortedArraysEqual(currentIds, newIds) ? currentIds : newIds,
  );
}

export function updateScheduledMessages<T extends GlobalState>(
  global: T, chatId: string, newById: Record<number, ApiMessage>,
): T {
  const current = global.scheduledMessages.byChatId[chatId] || { byId: {}, hash: 0 };

  return {
    ...global,
    scheduledMessages: {
      byChatId: {
        ...global.scheduledMessages.byChatId,
        [chatId]: {
          ...current,
          byId: {
            ...current.byId,
            ...newById,
          },
        },
      },
    },
  };
}

export function updateQuickReplyMessages<T extends GlobalState>(
  global: T, update: Record<number, ApiMessage>,
): T {
  return {
    ...global,
    quickReplies: {
      ...global.quickReplies,
      messagesById: {
        ...global.quickReplies.messagesById,
        ...update,
      },
    },
  };
}

export function updateFocusedMessage<T extends GlobalState>(
  global: T, update: Partial<TabState['focusedMessage']> | undefined, ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  if (!update) {
    return updateTabState(global, { focusedMessage: undefined }, tabId);
  }

  return updateTabState(global, {
    focusedMessage: {
      ...selectTabState(global, tabId).focusedMessage,
      ...update,
    },
  }, tabId);
}

export function updateSponsoredMessage<T extends GlobalState>(
  global: T, chatId: string, message: ApiSponsoredMessage,
): T {
  return {
    ...global,
    messages: {
      ...global.messages,
      sponsoredByChatId: {
        ...global.messages.sponsoredByChatId,
        [chatId]: message,
      },
    },
  };
}

export function deleteSponsoredMessage<T extends GlobalState>(
  global: T, chatId: string,
): T {
  const byChatId = global.messages.sponsoredByChatId;
  if (!byChatId[chatId]) {
    return global;
  }

  return {
    ...global,
    messages: {
      ...global.messages,
      sponsoredByChatId: omit(byChatId, [chatId]),
    },
  };
}

export function enterMessageSelectMode<T extends GlobalState>(
  global: T,
  chatId: string,
  messageId?: number | number[],
  ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  const messageIds = messageId ? Array.prototype.concat([], messageId) : [];

  return updateTabState(global, {
    selectedMessages: {
      chatId,
      messageIds,
    },
  }, tabId);
}

export function toggleMessageSelection<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  messageListType: MessageListType,
  messageId: number,
  groupedId?: string,
  childMessageIds?: number[],
  withShift = false,
  ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  const { selectedMessages: oldSelectedMessages } = selectTabState(global, tabId);
  if (groupedId) {
    childMessageIds = selectMessageIdsByGroupId(global, chatId, groupedId);
  }
  const selectedMessageIds = childMessageIds || [messageId];
  if (!oldSelectedMessages) {
    return enterMessageSelectMode(global, chatId, selectedMessageIds, tabId);
  }

  const { messageIds } = oldSelectedMessages;

  let newMessageIds;
  const newSelectedMessageIds = selectedMessageIds.filter((id) => !messageIds.includes(id));
  if (newSelectedMessageIds && !newSelectedMessageIds.length) {
    newMessageIds = messageIds.filter((id) => !selectedMessageIds.includes(id));
  } else if (withShift && messageIds.length) {
    const viewportIds = selectCurrentMessageIds(global, chatId, threadId, messageListType, tabId)!;
    const prevIndex = viewportIds.indexOf(messageIds[messageIds.length - 1]);
    const currentIndex = viewportIds.indexOf(messageId);
    const from = Math.min(prevIndex, currentIndex);
    const to = Math.max(prevIndex, currentIndex);
    const slice = viewportIds.slice(from, to + 1);
    newMessageIds = unique([...messageIds, ...slice]);
  } else {
    newMessageIds = [...messageIds, ...newSelectedMessageIds];
  }

  if (!newMessageIds.length) {
    return exitMessageSelectMode(global, tabId);
  }

  return updateTabState(global, {
    selectedMessages: {
      ...oldSelectedMessages,
      messageIds: newMessageIds,
    },
  }, tabId);
}

export function exitMessageSelectMode<T extends GlobalState>(
  global: T,
  ...[tabId = getCurrentTabId()]: TabArgs<T>
): T {
  return updateTabState(global, {
    selectedMessages: undefined,
  }, tabId);
}

export function addActiveMediaDownload<T extends GlobalState>(
  global: T,
  mediaHash: string,
  metadata: TabState['activeDownloads'][string],
  ...[tabId = getCurrentTabId()]: TabArgs<T>
) {
  const tabState = selectTabState(global, tabId);

  global = updateTabState(global, {
    activeDownloads: {
      ...tabState.activeDownloads,
      [mediaHash]: metadata,
    },
  }, tabId);

  return global;
}

export function addDownloadedMedia<T extends GlobalState>(global: T, mediaHash: string, fileName?: string): T {
  const value = fileName || true;
  if (global.downloadedMedia[mediaHash] === value) return global;

  return {
    ...global,
    downloadedMedia: {
      ...global.downloadedMedia,
      [mediaHash]: value,
    },
  };
}

export function cancelMessageMediaDownload<T extends GlobalState>(
  global: T,
  mediaHashes: string[],
  ...[tabId = getCurrentTabId()]: TabArgs<T>
) {
  const tabState = selectTabState(global, tabId);

  const newActiveDownloads = omit(tabState.activeDownloads, mediaHashes);

  global = updateTabState(global, {
    activeDownloads: newActiveDownloads,
  }, tabId);

  return global;
}

export function updateUploadByMessageKey<T extends GlobalState>(
  global: T,
  messageKey: MessageKey,
  progress: number | undefined,
) {
  return {
    ...global,
    fileUploads: {
      byMessageKey: progress !== undefined
        ? {
          ...global.fileUploads.byMessageKey,
          [messageKey]: { progress },
        }
        : omit(global.fileUploads.byMessageKey, [messageKey]),
    },
  };
}

export function updateQuickReplies<T extends GlobalState>(
  global: T,
  quickRepliesUpdate: Record<number, ApiQuickReply>,
) {
  return {
    ...global,
    quickReplies: {
      ...global.quickReplies,
      byId: {
        ...global.quickReplies.byId,
        ...quickRepliesUpdate,
      },
    },
  };
}

export function deleteQuickReply<T extends GlobalState>(
  global: T,
  quickReplyId: number,
) {
  return {
    ...global,
    quickReplies: {
      ...global.quickReplies,
      byId: omit(global.quickReplies.byId, [quickReplyId]),
    },
  };
}

export function updateFullWebPage<T extends GlobalState>(
  global: T,
  webPageId: string,
  update: Partial<ApiWebPageFull>,
) {
  const webpage = selectWebPage(global, webPageId);
  const updatedWebpage = webpage?.webpageType === 'full'
    ? { ...webpage, ...update }
    : { webpageType: 'full', mediaType: 'webpage', ...update };

  if (!updatedWebpage.id) {
    return global;
  }

  return replaceWebPage(global, webPageId, updatedWebpage as ApiWebPageFull);
}

export function replaceWebPage<T extends GlobalState>(
  global: T,
  webPageId: string,
  webPage: ApiWebPage,
) {
  return {
    ...global,
    messages: {
      ...global.messages,
      webPageById: {
        ...global.messages.webPageById,
        [webPageId]: webPage,
      },
    },
  };
}

export function updatePoll<T extends GlobalState>(
  global: T,
  pollId: string,
  pollUpdate: Partial<ApiMessagePoll>,
) {
  const poll = selectPoll(global, pollId);
  const results = mergePollResults(poll?.results, pollUpdate.results);

  if (!results) {
    return global;
  }

  const updatedPoll = {
    ...poll,
    ...pollUpdate,
    results,
  } satisfies ApiMessagePoll;
  if (!updatedPoll.summary?.id) {
    return global;
  }

  return {
    ...global,
    messages: {
      ...global.messages,
      pollById: {
        ...global.messages.pollById,
        [pollId]: updatedPoll,
      },
    },
  };
}

// The REST backend inlines the full poll on each history message (`content.poll`), but `pollById`
// is otherwise only fed by live updates — so on reload polls would miss. Lift any inlined poll into
// `pollById` (without clobbering an entry a live update may have set fresher). `content.poll` is
// off the `MediaContent` type, so it's read defensively here.
export function addPollsFromMessages<T extends GlobalState>(global: T, messages: ApiMessage[]): T {
  let added: Record<string, ApiMessagePoll> | undefined;

  for (const message of messages) {
    const inlinePoll = (message.content as { poll?: ApiMessagePoll }).poll;
    const pollId = inlinePoll?.summary?.id;
    // Always refresh from the freshly-fetched inline poll — the existing `pollById` entry may be a
    // stale/answerless cached value, which (if skipped) renders as just the question on reload.
    if (!pollId) continue;

    if (!added) added = {};
    added[pollId] = inlinePoll;
  }

  if (!added) return global;

  return {
    ...global,
    messages: {
      ...global.messages,
      pollById: {
        ...global.messages.pollById,
        ...added,
      },
    },
  };
}

function mergePollResults(
  currentResults: ApiPollResults | undefined,
  newResults: ApiPollResults | undefined,
) {
  if (!newResults) {
    return currentResults;
  }

  if (!currentResults) {
    return newResults;
  }

  const mergedResults: ApiPollResults = {
    ...currentResults,
    ...newResults,
  };

  if (newResults.resultByOption) {
    mergedResults.resultByOption = newResults.isMin
      ? mergeMinPollResultByOption(currentResults.resultByOption, newResults.resultByOption)
      : newResults.resultByOption;
  }

  return mergedResults;
}

function mergeMinPollResultByOption(
  currentResultByOption: Record<string, ApiPollResult> | undefined,
  newResultByOption: Record<string, ApiPollResult>,
) {
  const mergedResultByOption: Record<string, ApiPollResult> = {};

  Object.keys(newResultByOption).forEach((option) => {
    const currentResult = currentResultByOption?.[option];
    const nextResult = newResultByOption[option];

    if (!currentResult) {
      mergedResultByOption[option] = nextResult;
      return;
    }

    mergedResultByOption[option] = {
      ...currentResult,
      ...nextResult,
      isChosen: currentResult.isChosen || nextResult.isChosen,
      isCorrect: currentResult.isCorrect || nextResult.isCorrect,
    };
  });

  return mergedResultByOption;
}

export function updatePollVote<T extends GlobalState>(
  global: T,
  pollId: string,
  peerId: string,
  options: string[],
) {
  const poll = selectPoll(global, pollId);
  if (!poll) {
    return global;
  }

  const { recentVoterIds, totalVoters, resultByOption } = poll.results;
  const newRecentVoterIds = recentVoterIds ? [...recentVoterIds] : [];
  const newTotalVoters = totalVoters ? totalVoters + 1 : 1;
  const newResultByOption = { ...resultByOption };

  newRecentVoterIds.push(peerId);

  options.forEach((option) => {
    const targetOption = resultByOption?.[option];
    const updatedOption: ApiPollResult = targetOption ? { ...targetOption } : { option, votersCount: 0 };
    const recentOptionVoterIds = updatedOption.recentVoterIds ? [...updatedOption.recentVoterIds] : [];

    updatedOption.votersCount += 1;
    recentOptionVoterIds.push(peerId);
    updatedOption.recentVoterIds = recentOptionVoterIds;
    if (peerId === global.currentUserId) {
      updatedOption.isChosen = true;
    }

    newResultByOption[option] = updatedOption;
  });

  return updatePoll(global, pollId, {
    results: {
      ...poll.results,
      recentVoterIds: newRecentVoterIds,
      totalVoters: newTotalVoters,
      resultByOption: newResultByOption,
    },
  });
}

export function updateTypingDraft<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId | undefined = MAIN_THREAD_ID,
  randomId: string,
  text: ApiFormattedText,
) {
  const typingDraftStore = selectThreadLocalStateParam(global, chatId, threadId, 'typingDraftIdByRandomId');
  const messageId = typingDraftStore?.[randomId];
  if (!messageId) {
    return global;
  }

  global = updateChatMessage(global, chatId, messageId, {
    content: {
      text,
    },
  });
  return global;
}
